import React from 'react';
import { Link } from 'react-router-dom';
import { Tooltip } from 'ksnm-common-ui/lib/Tooltip';
import type { ColumnInterface } from 'ksnm-common-ui/lib/Table2/Table.interface';
import type { FieldFormat, ResponseDataRows } from 'ksnm-common-ui/lib/Table2/utils/TableRequest';

import { RoutePath } from '@components/AppRouter/constants';

import { Status } from '../../DataControl/Status';
import { NotRunningIcon } from './Icons/NotRunningIcon';
import { UnattachedIcon } from './Icons/UnattachedIcon';

import styles from '../PreparationOfReports.module.scss';

export const getColumns = (localData: ResponseDataRows): ColumnInterface[] => [
  {
    key: 'name1',
    title: 'Организация',
    groupBy: 10,
    hidden: true,
  },
  {
    key: 'name2',
    title: 'Год',
    groupBy: 20,
    hidden: true,
  },
  {
    key: 'name3',
    title: 'Код группы',
    groupBy: 30,
    hidden: true,
  },
  {
    key: 'name4',
    title: 'Код пакета',
    groupBy: 40,
    hidden: true,
  },
  {
    key: 'name5',
    title: 'Журнал',
    groupBy: 50,
    hidden: true,
  },
  {
    key: 'ctrlStatus',
    title: 'Статус прохождения КП',
    width: 250,
    render: ({ data }) => {
      let status = data.ctrlStatus ? `${data.ctrlStatus} - ${data.ctrlStatusText}` : '';
      let statusType = data.ctrlStatusType;
      let storageData = data;
      let controlLevel = true;

      let execCtrlAmount = (data.execCtrlAmount as number) ?? 0;
      let ctrlAmount = (data.ctrlAmount as number) ?? 0;
      let ctrlConfiguredAmount = (data.ctrlConfiguredAmount as number) ?? 0;

      if (data.is_group && data.level === 4) {
        const found = localData?.find((f) => f.name4 === data.name4);

        if (found) {
          status = `${found.cpckgStatus} - ${found.cpckgStatusText}`;
          statusType = found.cpckgStatusType;
          storageData = found;
          execCtrlAmount = (found.execCtrlAmount as number) ?? 0;
          ctrlAmount = (found.ctrlAmount as number) ?? 0;
          ctrlConfiguredAmount = (found.ctrlConfiguredAmount as number) ?? 0;

          controlLevel = false;
        }
      }

      const setStorage = () =>
        localStorage.setItem('passingControlData', JSON.stringify({ ...storageData, controlLevel }));

      return (
        <div className={styles.statusWithIcon}>
          <Status status={statusType}>
            {data.ctrlStatus !== 'O' ? (
              <Tooltip content="Перейти в Результаты выполнения КП">
                <Link
                  className={styles.link}
                  onContextMenu={setStorage}
                  onMouseUp={setStorage}
                  onClick={(e) => {
                    e.stopPropagation();
                    setStorage();
                  }}
                  to={{ pathname: RoutePath.DATA_CONTROL }}
                >
                  {status}
                </Link>
              </Tooltip>
            ) : (
              status
            )}
          </Status>

          {/* Выводим только для пакетов */}
          {status && data.is_group && data.level === 4 && (
            <div className={styles.icons}>
              {execCtrlAmount < ctrlAmount && (
                <Tooltip content={`Не все контроли запущены (${ctrlAmount - execCtrlAmount})`} hideOnClick={false}>
                  <span className={styles.unattachedIcon}>
                    <UnattachedIcon />
                  </span>
                </Tooltip>
              )}

              {ctrlAmount < ctrlConfiguredAmount && (
                <Tooltip
                  content={`Есть непривязанные к пакету контроли (${ctrlConfiguredAmount - ctrlAmount})`}
                  hideOnClick={false}
                >
                  <span className={styles.notRunningIcon}>
                    <NotRunningIcon />
                  </span>
                </Tooltip>
              )}
            </div>
          )}
        </div>
      );
    },
  },
  {
    key: 'ctrlVersDesc',
    title: 'Наименование',
  },
  {
    key: 'periodKp',
    title: 'Период',
    width: 200,
    render: ({ data }) => {
      if (data.is_group && data.level === 4) {
        const found = localData?.find((f) => f.name4 === data.name4);

        if (found) return found.periodKp;
      }
    },
  },
  {
    key: 'ctrlDateKp',
    title: 'Дата прохождения КП',
    width: 220,
    render: ({ data, value }) => {
      if (data.is_group && data.level === 4) {
        const found = localData?.find((f) => f.name4 === data.name4);

        if (found) return found.cpckgDateKp;
      }

      return value;
    },
  },
  {
    key: 'ctrlUserKp',
    title: 'Пользователь',
    width: 300,
    render: ({ value, data }) => {
      if (data.is_group && data.level === 4) {
        const found = localData?.find((f) => f.name4 === data.name4);

        if (found) return found.cpckgUserKp;
      }

      return value;
    },
  },
];

export const fieldFormat: { [index: string]: FieldFormat } = {
  id: 'number',
  reportTypeOrder: 'number',
  name: 'string',
  reportPackageCode: 'string',
  reportPackageStatus: 'string',
  reportGroupCode: 'string',
  periodCode: 'string',
  reportPackageName: 'string',
  reportPackageCorrection: 'string',
  reportPackageYear: 'number',
  preparationMonth: 'number',
  preparationDay: 'number',
  disclosureMonthCode: 'number',
  dueDay: 'number',
  reportPublishDate: 'string',
  reportPackageUser: 'string',
  reportPackageDeclarationRegNumber: 'string',
  organization: 'string',
  reportCodeExt: 'string',
  reportPublishStatus: 'string',
  reportName: 'string',
  infoSystemCode: 'string',
  reportPublishUser: 'string',
  buildDate: 'string',
  uploadDate: 'string',
  organizationCode: 'string',
  reportVersionCode: 'string',
  reportVersionName: 'string',
  reportCode: 'string',
  taxTypeCode: 'string',
  taxTypeName: 'string',
  reportTypeCode: 'string',
  reportVersionVersion: 'string',
  orderNum: 'number',
  name1: 'string',
  name2: 'string',
  name3: 'string',
  name4: 'string',
  name5: 'string',
  ctrlDateKp: 'timestamp',
};
